import React, { useEffect, useRef, useState } from 'react'

/**
 * Edit in place (owner 2026-09-05: the page is the form). A value on the
 * page is the trigger; pressing it opens a small popover with the same
 * controls the create form uses, and an explicit Save — nothing saves on
 * blur, so a stray click never rewrites a task. Escape or an outside tap
 * closes without saving.
 */
interface Props {
  /** Accessible name for the trigger and the dialog, e.g. "When". */
  label: string
  /** What the page shows: the current value, or an "add" chip. */
  trigger: React.ReactNode
  /** dotted = a value in running text; chip = an "+ add" pill. */
  variant?: 'dotted' | 'chip' | 'icon'
  /** Called when the popover opens — reset the draft here. */
  onOpen?: () => void
  /** Persist the draft; throw to show the message and stay open. */
  onSave: () => Promise<void>
  /** Hide Save (a body that saves itself, e.g. a picker with its own button). */
  saveLabel?: string | null
  disabled?: boolean
  width?: string
  /**
   * Which edge of the trigger the panel hangs from. Default 'left' suits a
   * value sitting in running text. Settings rows put the value hard against
   * the right margin, where a left-anchored panel runs off a phone screen —
   * `popover-fit` caps the WIDTH but cannot pull the panel back on screen.
   */
  align?: 'left' | 'right'
  className?: string
  testId?: string
  /** Bump to open from outside (e.g. "rename it" from the gaps card). */
  openRequest?: number
  children: React.ReactNode
}

const TRIGGER: Record<NonNullable<Props['variant']>, string> = {
  dotted: 'rounded px-0.5 text-left underline decoration-dotted underline-offset-2 hover:text-brand',
  chip: 'inline-flex items-center gap-1 rounded-full border border-dashed border-strong px-2.5 py-1 text-app-micro font-medium text-secondary hover:border-brand hover:text-brand',
  icon: 'inline-flex items-center justify-center rounded p-0.5 text-muted hover:text-brand',
}

export function InlinePopover({ label, trigger, variant = 'dotted', onOpen, onSave, saveLabel = 'Save', disabled = false, width = 'w-80', align = 'left', className = '', testId, openRequest = 0, children }: Props) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const rootRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (!openRequest || disabled) return
    setErr(null); onOpen?.(); setOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open on the request, not on every render of onOpen
  }, [openRequest])

  const toggle = () => {
    if (disabled) return
    if (!open) { setErr(null); onOpen?.() }
    setOpen((o) => !o)
  }

  const save = async () => {
    setBusy(true); setErr(null)
    try {
      await onSave()
      setOpen(false)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save that')
    } finally {
      setBusy(false)
    }
  }

  return (
    <span ref={rootRef} className={`relative inline-block max-w-full ${className}`}>
      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        title={disabled ? undefined : `Change: ${label}`}
        className={`${TRIGGER[variant]} disabled:no-underline disabled:hover:text-inherit`}
        data-testid={testId}
      >
        {trigger}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={label}
          className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full z-30 mt-1 ${width} popover-fit rounded-lg border border-soft bg-surface p-3 text-left shadow-lg`}
          data-testid={testId ? `${testId}-popover` : undefined}
        >
          <div className="text-app-micro font-bold uppercase tracking-widest text-secondary">{label}</div>
          <div className="mt-2 space-y-2">{children}</div>
          {err && <p className="mt-2 text-xs text-bad" role="alert">{err}</p>}
          <div className="mt-3 flex items-center gap-2">
            {saveLabel !== null && (
              <button type="button" onClick={() => void save()} disabled={busy} className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-on-brand hover:opacity-90 disabled:opacity-60" data-testid={testId ? `${testId}-save` : undefined}>
                {busy ? 'Saving…' : saveLabel}
              </button>
            )}
            <button type="button" onClick={() => setOpen(false)} disabled={busy} className="px-2 py-1.5 text-sm text-muted hover:text-secondary">Cancel</button>
          </div>
        </div>
      )}
    </span>
  )
}

/** One row of choices, tap to pick, tap again to clear — the form's chips. */
export function ChipRow({ options, value, onChange, label, small = true }: { options: { value: string; label: string }[]; value: string; onChange: (v: string) => void; label: string; small?: boolean }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label={label}>
      {options.map((o) => {
        const on = value === o.value
        return (
          <button key={o.value} type="button" aria-pressed={on} onClick={() => onChange(on ? '' : o.value)}
                  className={`rounded-full border font-medium transition-colors ${small ? 'px-2.5 py-1 text-app-micro' : 'px-3.5 py-2 text-sm'} ${
                    on ? 'border-brand bg-brand text-on-brand' : 'border-soft bg-surface text-secondary hover:border-brand'}`}>
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

export default InlinePopover
